import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import { useIsBreakpoint } from "../../../../utils/V2/screen"
import CircledIconButton from "../../../elements/V2/Buttons/CircledIconButton"
import ClickableCardLink from "../../../elements/V2/ClickableCardLink"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Typography from "../../../elements/V2/Typography"
import Video from "../../../experiments/Video"
import LayoutItem from "../../Layouts/LayoutItem"

import {
  getBackgroundColorClass,
  getGradientFromColorClass,
  getGradientToColorClass,
} from "@utils/V2/color"
import React, { useCallback, useState } from "react"

type Props = {
  primaryBackgroundColor?: Color
  secondaryBackgroundColor?: Color
  textColor: Color.White | Color.Charcoal
  superscriptText: string
  titleText: string
  descriptionText: string
  superscriptTextColor?: Color
  size:
    | "col-span-3"
    | "col-span-4"
    | "col-span-6"
    | "col-span-8"
    | "col-span-12"
  videoUrl?: string
  imageUrl: string
  imageAlt: string
  imageRounded: boolean
  backgroundImage: boolean
  buttonColor?: Color
  buttonLink?: string | YocoCom.YocoLink
  mobileGridOrder?: number
  isSliderChild?: boolean
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
}

const Content = ({
  primaryBackgroundColor,
  secondaryBackgroundColor,
  textColor,
  superscriptText,
  titleText,
  descriptionText,
  superscriptTextColor,
  size,
  videoUrl,
  imageUrl,
  imageAlt,
  imageRounded,
  backgroundImage,
  buttonColor,
  buttonLink,
  mobileGridOrder,
  isSliderChild,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  ...props
}: Props) => {
  const isDesktop = useIsBreakpoint("desktop")
  const [videoContainerHover, setVideoContainerHover] = useState(false)

  const onMouseEnter = useCallback(() => {
    setVideoContainerHover(true)
  }, [])

  const onMouseLeave = useCallback(() => {
    setVideoContainerHover(false)
  }, [])

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      isSliderChild={isSliderChild}
      onMouseEnter={videoUrl ? onMouseEnter : undefined}
      onMouseLeave={videoUrl ? onMouseLeave : undefined}
      className={classNames(
        "group",
        buttonLink && "cursor-pointer",
        "transition-shadow duration-300 hover:hover:shadow-xl",
        !secondaryBackgroundColor && primaryBackgroundColor && !videoUrl
          ? getBackgroundColorClass(primaryBackgroundColor)
          : secondaryBackgroundColor && primaryBackgroundColor && !videoUrl
            ? [
                "bg-gradient-to-br",
                getGradientFromColorClass(primaryBackgroundColor),
                getGradientToColorClass(secondaryBackgroundColor),
              ]
            : "",
        "rounded-lg relative col-span-full flex grid-rows-[max-content_1fr] flex-col justify-between overflow-hidden sm:col-span-2 md:col-span-3",
        size ? `lg:${size}` : "lg:col-span-4"
      )}
      {...props}
    >
      <div
        className={classNames(
          "z-10 flex flex-col gap-16 px-20 py-24 md:px-24 md:py-32 lg:px-32 lg:py-48",
          { absolute: backgroundImage || videoUrl }
        )}
      >
        <Typography
          font="grotesk"
          weight="book"
          size="subscript-lg"
          color={superscriptTextColor || textColor}
          text={superscriptText}
        />
        <Typography
          font="grotesk"
          weight="book"
          size="lead-lg"
          color={textColor}
          text={titleText}
        />
        <Typography
          font="grotesk"
          weight="book"
          size="body-lg"
          color={textColor}
          text={descriptionText}
        />
      </div>

      {videoUrl ? (
        <>
          <div className="absolute inset-0 flex items-center justify-center">
            <GatsbyStoryblokImage
              className="transition-transform duration-500 ease-out group-hover:scale-105"
              aspectRatio={backgroundImage ? "3:4" : "4:3"}
              image={imageUrl}
              alt={imageAlt}
              width={495}
              rounded={imageRounded}
            />
          </div>
          <Video
            video={videoUrl}
            coverImage={imageUrl}
            ImageAlt={imageAlt}
            aspectRatio="3:4"
            playbackMode={isDesktop ? "hover" : "autoplay"}
            videoContainerHover={videoContainerHover}
            rounded={true}
          />
        </>
      ) : (
        <GatsbyStoryblokImage
          className="transition-transform duration-500 ease-out group-hover:scale-105"
          aspectRatio={backgroundImage ? "3:4" : "4:3"}
          image={imageUrl}
          alt={imageAlt || "Benefit image"}
          width={495}
          rounded={imageRounded}
        />
      )}
      {buttonColor && buttonLink && (
        <div className="absolute bottom-20 right-20 md:bottom-24 md:right-24 lg:bottom-32 lg:right-32">
          <CircledIconButton
            color={buttonColor}
            style="bordered"
            decorative={true}
            fillOnGroupHover={true}
          />
        </div>
      )}

      {buttonLink && (
        <ClickableCardLink
          link={buttonLink}
          ariaLabel={titleText}
          trackingEvent={trackingEvent}
          trackingEventKey={trackingEventKey}
          trackingEventValue={trackingEventValue}
        />
      )}
    </LayoutItem>
  )
}

export default Content
